import { useMemo } from 'react';
import { useABTest } from '@bridebook/toolbox/src/ab-testing/lib/hooks/use-ab-test';
import { useInitializeUserId } from '@bridebook/toolbox/src/ab-testing/lib/hooks/use-intitialize-user-id';
import { loggedOutOnlyPages } from '@bridebook/web/lib/url-helper';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { BASIC_USER_ID_COOKIE } from './use-ab-test-integration';

/**
 * Activates an AB Test with per-test loading state tracking.
 *
 * Unlike `useABTestIntegration`, this hook tracks detailed per-test status
 * (loading / loaded / error) and provides `isInitialized` so consumers
 * can handle a loading state instead of prematurely rendering control.
 *
 * @param testId - id of the test from firestore
 * @param triggerCondition (optional)
 * - undefined - trigger is not ready
 * - false - shouldn't be triggered
 * - true - should be triggered
 * @param triggerForLoggedOutUsers (optional) - flag to trigger the test for logged out users (eg /signup page)
 */
export const useABTestV2Integration = ({
  testId,
  triggerCondition,
  triggerForLoggedOutUsers,
}: {
  testId: string;
  triggerCondition?: boolean;
  triggerForLoggedOutUsers?: boolean;
}) => {
  const loggedInUserId = useSelector(getCurrentUserId);
  const path = usePathWithoutMarket();

  const isOnLoggedOutPage = useMemo(
    () => loggedOutOnlyPages.some((page) => path.includes(page)),
    [path],
  );

  const userId = useInitializeUserId(
    BASIC_USER_ID_COOKIE,
    loggedInUserId,
    triggerForLoggedOutUsers,
    isOnLoggedOutPage,
  );

  return useABTest({
    triggerCondition,
    userId,
    testId,
  });
};
